// src/api/microsoftcalendar.ts
import api from "./client";
import { config } from "../config";

export type CalendarProvider = "google" | "microsoft" | null;

export type BusinessCalendar = {
  business_calendar_id: string;
  provider: "google" | "microsoft";
  provider_calendar_id: string;
  calendar_name: string;
  timezone: string | null;
  color: string | null;
  is_bookable: boolean;
  is_default: boolean;
};

/**
 * Step 1: Launch Microsoft OAuth flow (browser redirect)
 */
export function connectMicrosoftCalendar(state?: string) {
  const params = new URLSearchParams({
    client_id: config.microsoft.clientId,
    response_type: "code",
    redirect_uri: config.microsoft.redirectUri,
    response_mode: "query",
    scope: ["offline_access", "Calendars.ReadWrite", "User.Read"].join(" "),
    prompt: "consent",
  });

  if (state) params.set("state", state);

  window.location.href = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?${params.toString()}`;
}

/**
 * Step 2: Complete connect on our API
 */
export async function completeMicrosoftConnect(code: string) {
  try {
    const response = await api.post("/connect_calendar", {
      provider: "microsoft",
      code,
    });

    return response.data;
  } catch (error: any) {
    console.error("❌ Error completing Microsoft connect:", error);
    throw error.response?.data || error;
  }
}

/**
 * Disconnect Microsoft Calendar.
 */
export async function disconnectMicrosoftCalendar() {
  try {
    const response = await api.post("/disconnect_calendar", {
      provider: "microsoft",
    });

    return response.data;
  } catch (error: any) {
    console.error("❌ Error disconnecting Microsoft calendar:", error);
    throw error.response?.data || error;
  }
}

/**
 * Fetch all business calendars for the current business.
 */
export async function fetchBusinessCalendars(): Promise<BusinessCalendar[]> {
  try {
    const response = await api.get("/business_calendars");
    return response.data;
  } catch (error: any) {
    console.error("❌ Error fetching business calendars:", error);
    throw error.response?.data || error;
  }
}

/**
 * Update a specific business calendar (is_bookable / is_default flags).
 */
export async function updateBusinessCalendar(
  businessCalendarId: string,
  patch: Partial<Pick<BusinessCalendar, "is_bookable" | "is_default">>
) {
  try {
    const response = await api.patch(
      `/business_calendars/${businessCalendarId}`,
      patch
    );

    return response.data;
  } catch (error: any) {
    console.error("❌ Error updating business calendar:", error);
    throw error.response?.data || error;
  }
}
